// La fiche devis : en-tête, pièces, enregistrement.
//
// L'enregistrement se fait en UN appel transactionnel. Le prix affiché
// pendant la saisie vient de `shared/pricing.js` côté navigateur ; le
// prix qui fait foi est celui que le serveur renvoie, calculé par le
// même module. Les deux coïncident au centime, par construction.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Card, CardBody } from '../../ui/Card.jsx';
import { Select } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import PieceEditor from './PieceEditor.jsx';
import BandePieces, { etatDePiece } from './BandePieces.jsx';
import BarreChiffrage from './BarreChiffrage.jsx';
import PanneauAssistant from '../assistant/PanneauAssistant.jsx';
import ProgressionPages from './ProgressionPages.jsx';
import { useTraitement } from './useTraitement.js';
import DocumentsCard from './DocumentsCard.jsx';
import PlanUploadCard from './PlanUploadCard.jsx';
import DevisHeaderCard from './DevisHeaderCard.jsx';
import PlanEtFiche from './PlanEtFiche.jsx';
import ConsigneGamme from './ConsigneGamme.jsx';
import DiffGamme from './DiffGamme.jsx';
import { useDevisEditor } from './useDevisEditor.js';
import { useEnregistrement } from './useEnregistrement.js';
import { OPTIONS_STATUT, estVerrouille } from './statuts.js';
import { useResource } from '../../lib/useResource.js';
import { pricingDefaultsFrom } from '@shared/pricing.js';
import { consigneAvecBrutDeCorrection } from '@shared/consigneBrut.js';
import { api, READ_TIMEOUT_MS } from '../../lib/api.js';
import { gammeProposee } from './gammeProposee.js';
import { formatEuro } from '../../lib/format.js';
import './devis.css';


export function DevisPage() {
  const { id } = useParams();
  const load = useCallback(
    () =>
      Promise.all([
        api.get(`/devis/${id}`),
        api.get('/parametres'),
        // Le référentiel matières : le rattachement d'un code client se
        // fait DEPUIS l'écran de chiffrage, là où le manque se voit. Un
        // deviseur qui doit quitter son devis pour créer un alias ne le
        // fera pas — il tapera la densité à la main, et la boucle
        // d'apprentissage n'aura jamais lieu.
        api.get('/catalogue/materials'),
      ]).then(([devis, settings, materiaux]) => ({
        ...devis, settings, materiaux: materiaux.materials ?? [],
      })),
    [id],
  );
  // [id] : passer d'un devis à sa copie (révision, nouvelle demande) garde la route.
  const { data, error, loading, reload } = useResource(load, [id]);
  if (loading) return <SkeletonRows rows={6} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  return <DevisEditor key={data.devis.id} data={data} onReload={reload} />;
}

function DevisEditor({ data, onReload: recharger }) {
  const toast = useToast();
  /**
   * LA sélection de l'écran, et il n'y en a qu'une. Deux états (page
   * affichée, dernière lecture) pouvaient diverger : le plan d'une pièce
   * avec les chiffres d'une autre, sans aucun signe (§11, ligne 13).
   * Une seule sélection gouverne le plan, la lecture, les temps et le prix.
   */
  const [selection, setSelection] = useState(0);
  const navigate = useNavigate();
  // L'assemblage vient de `shared/`, comme la formule de prix elle-même.
  // Cette ligne lisait `settings.pricing`, où les TAUX ne sont pas : ils
  // vivent dans `settings.rates`. L'écran calculait donc à 0 €/h et
  // affichait « Taux machine absent » sur un écran où il est présent.
  const defaults = pricingDefaultsFrom(data.settings.settings);
  const editor = useDevisEditor(data.devis, data.pieces, defaults);
  const locked = estVerrouille(editor.devis.status);
  const enreg = useEnregistrement(editor, { locked, toast });
  const onReload = () => enreg.vider().then(() => recharger()); // démonte l'éditeur : enregistrer d'abord

  // La sélection est bornée par ce qui existe : supprimer la dernière
  // pièce ne doit pas laisser un index qui pointe dans le vide — c'est
  // exactement le motif « un composant qui garde son dernier état quand
  // la donnée courante est absente ».
  const index = Math.min(selection, Math.max(0, editor.priced.length - 1));
  useEffect(() => {
    if (selection !== index) setSelection(index);
  }, [selection, index]);

  // ── Le déroulé, suivi à l'écran ──
  //
  // Le dépôt lance TOUT côté serveur ; ce hook regarde où ça en est et
  // recharge le devis quand c'est fini. C'est le chaînon qui manquait :
  // le pipeline enchaînait, l'écran ne le savait pas.
  const traitement = useTraitement(data.devis.id, { onTermine: onReload });
  // DÉCOUPE (14/10/2026) : enregistrer avant, recharger après SANS réenregistrer.
  const decoupe = {
    devisId: data.devis.id,
    avant: async () => { if (!(await enreg.vider())) throw new Error('Enregistrez le devis avant de découper.'); },
    apres: () => { enreg.liberer(); recharger(); },
  };

  const courante = editor.priced[index] ?? null;
  const aVerifier = editor.priced.filter((p) => p.piece?.a_verifier).length;
  // Ce qui reste à chiffrer — le MÊME état que l'étiquette de la bande,
  // calculé par la même fonction. Deux définitions du même mot
  // finiraient par se contredire à l'écran.
  const aChiffrer = editor.priced
    .filter((p) => etatDePiece(p.piece, p.result) === 'a_estimer').length;
  // Le visualiseur ne reçoit QUE la page de la pièce courante (invariant 13).
  const pagesDeLaPiece = courante?.piece?.plan_file_id
    ? [{ id: courante.piece.plan_file_id, page_number: courante.piece.plan_index ?? null }]
    : [];

  // ── LE PLAN S'AFFICHE DÈS LE DÉPÔT, pas à la fin de la lecture ──
  //
  // Le visualiseur était branché sur la PIÈCE. Or une pièce naît de la
  // lecture, et une lecture prend jusqu'à 108 secondes par page : on
  // déposait un PDF et on regardait une barre de progression pendant
  // deux minutes, alors que le dessin était déjà découpé et prêt à
  // l'écran. Le §0 du produit dit « il dépose un plan et voit les
  // chiffres arriver » — pas « il attend, puis il voit tout d'un coup ».
  //
  // Les pages existent en base dès le dépôt (`files.parent_file_id`),
  // donc il n'y a rien à attendre. On les montre.
  //
  // L'invariant 13 tient : quand aucune pièce n'est sélectionnée, le
  // visualiseur ne porte AUCUNE référence de pièce — il dit « Page 2 »,
  // et le bandeau dit que la lecture est en cours. Un plan affiché sous
  // une référence qui n'est pas la sienne est exactement le défaut qu'on
  // a mis le plus longtemps à voir.
  const pagesDeposees = (traitement.pages ?? []).map((p) => ({
    id: p.id, page_number: p.page_number,
  }));
  const [pageDeposee, setPageDeposee] = useState(null);
  const enAttenteDePiece = !courante && pagesDeposees.length > 0;
  const pagesVues = courante ? pagesDeLaPiece : pagesDeposees;
  const pageVue = courante
    ? (courante.piece.plan_file_id ?? null)
    : (pagesDeposees.some((p) => p.id === pageDeposee) ? pageDeposee : pagesDeposees[0]?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [estimating, setEstimating] = useState(null);
  const [chiffrageDevis, setChiffrageDevis] = useState(false);
  // ── LA RÉVISION EN ATTENTE — §8.6 ──
  //
  // Elle vit ICI et pas dans la pièce : ce n'est pas une donnée de la
  // pièce tant qu'elle n'est pas appliquée. La mettre dans l'éditeur la
  // ferait partir à l'enregistrement, c'est-à-dire s'appliquer sans
  // qu'on l'ait acceptée — exactement ce que le diff existe pour
  // empêcher.
  const [revision, setRevision] = useState(null);
  const [revising, setRevising] = useState(false);
  const [machines, setMachines] = useState([]);
  const [suppliers, setSuppliers] = useState([]);


  useEffect(() => {
    // Le parc machines alimente les taux proposés dans la gamme, les
    // fournisseurs les délais proposés en sous-traitance.
    // Pas de repli sur une liste inventée : si l'appel échoue, la liste
    // reste vide, les valeurs se saisissent à la main, et ça se voit.
    api
      .get('/machines')
      .then((r) => setMachines(r.machines ?? []))
      .catch(() => setMachines([]));
    api
      .get('/catalogue/suppliers')
      .then((r) => setSuppliers(r.suppliers ?? []))
      .catch(() => setSuppliers([]));
  }, []);

  // L'enregistrement et les gardes de navigation : `useEnregistrement.js`.
  async function save() {
    setSaving(true);
    try {
      return await enreg.enregistrer({ silencieux: false });
    } finally {
      setSaving(false);
    }
  }

  /**
   * Demande une estimation à l'agent 2 pour une pièce.
   *
   * Elle remplit la colonne « valeur IA » et les prises ; elle ne
   * touche pas les temps retenus. C'est le deviseur qui valide.
   */
  /**
   * CHIFFRER TOUT LE DEVIS — 27 août 2026
   *
   * Depuis ce jour le dépôt ne lance plus que la lecture : la consigne
   * ne peut pas exister au moment où on estime. Mais sur un PDF de
   * vingt-deux pages, cliquer vingt-deux fois n'est pas un progrès.
   *
   * Ne chiffre QUE ce qui ne l'est pas — une pièce déjà estimée ne se
   * réestime que sur elle-même, explicitement. Le travail part en fond
   * et l'écran se rafraîchit : une estimation dure une minute et demie,
   * vingt-deux en prendraient trente.
   */
  async function chiffrerTout() {
    setChiffrageDevis(true);
    try {
      const res = await api.post(`/devis/${editor.devis.id}/pieces/chiffrage`, {},
        { timeout: READ_TIMEOUT_MS });
      if (!res.lancees) {
        toast.success('Toutes les pièces sont déjà chiffrées.');
        return;
      }
      toast.success(
        `Chiffrage lancé sur ${res.lancees} pièce(s). Les temps arrivent au fur et à mesure — `
        + 'rafraîchissez dans une minute.',
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setChiffrageDevis(false);
    }
  }

  async function estimer(index) {
    const piece = editor.pieces[index];
    setEstimating(index);
    try {
      const res = await api.post(
        `/devis/${editor.devis.id}/pieces/${piece.id ?? 0}/temps`,
        piece,
        { timeout: READ_TIMEOUT_MS },
      );
      editor.applyEstimation(index, res.estimation);
      enreg.bientot(); // une gamme payée ne vit pas que dans l'écran
      // Les avertissements de l'agent ne s'affichaient NULLE PART (6 octobre
      // 2026) — dont « brut incomplet : il manque l'épaisseur ».
      if (res.estimation.warnings?.length) toast.warn(res.estimation.warnings.join(' — '));
      toast.success(
        `Gamme proposée : ${(res.estimation.gamme ?? []).length} opérations` +
          (res.references_lues ? ` — ${res.references_lues} gamme(s) de l’atelier relue(s)` : '')
          + (res.corrections_lues ? ` — ${res.corrections_lues} correction(s) relue(s).` : '.'),
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEstimating(null);
    }
  }

  /**
   * Demande à l'agent de RÉVISER la gamme à partir de la consigne.
   *
   * Rien n'est appliqué : la réponse porte la gamme révisée ET son diff,
   * calculé en code. Le deviseur voit ce qui entre, ce qui sort et ce
   * qui bouge, puis il applique — ou il abandonne.
   */
  async function reviser(index, consigne) {
    const piece = editor.pieces[index];
    setRevising(true);
    setRevision(null);
    try {
      const res = await api.post(
        `/devis/${editor.devis.id}/pieces/${piece.id ?? 0}/gamme/revision`,
        { ...piece, consigne_revision: consigne },
        { timeout: READ_TIMEOUT_MS },
      );
      // ── LA CONSIGNE RÉALIGNE TOUT, TOUT DE SUITE ──
      //
      // Elle attendait un second clic sur « Appliquer ». Sur AFU00498 :
      // quinze révisions, aucune appliquée, et une gamme restée sur la
      // machine que la consigne demandait justement de changer. Le
      // deviseur en concluait que l'agent ne l'écoutait pas — et de son
      // point de vue c'était vrai, puisque rien ne bougeait à l'écran.
      //
      // Toujours la dernière gamme ; le diff se consulte, replié.
      appliquerGamme(index, res.estimation?.gamme ?? []);
      // La phrase qui a corrigé la gamme voyage avec la pièce jusqu'à
      // l'enregistrement, où elle devient la justification de la
      // correction (`ai_corrections`, réutilisable sur les comparables).
      // Elle part à part (`consigne_revision`) : la description aussi, et
      // un brut qu'elle annonce rejoint la description (2026-10-015).
      editor.patchPiece(index, {
        ...consigneAvecBrutDeCorrection(editor.pieces[index]?.consigne_gamme, consigne),
        revisions: [...(editor.pieces[index]?.revisions ?? []), consigne].slice(-20),
      });
      setRevision({ index, ...res });
      enreg.bientot();
      const r = res.diff?.resume ?? {};
      toast.success(
        `Gamme révisée et appliquée : ${r.ajoutees ?? 0} ajoutée(s), `
        + `${r.retirees ?? 0} retirée(s), ${r.modifiees ?? 0} modifiée(s).`,
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevising(false);
    }
  }

  /**
   * APPLIQUER : la gamme révisée devient la gamme, et les temps — donc
   * le prix — suivent tout seuls, `computePiece` les dérivant de la
   * gamme.
   *
   * Les lignes issues de la consigne sont marquées `consigne` : c'est
   * ce marquage qui, à l'enregistrement, deviendra la correction
   * réinjectable. Une gamme corrigée sans trace de ce qui l'a corrigée
   * n'apprend rien.
   */
  /**
   * La gamme révisée devient LA gamme, et les temps — donc le prix —
   * suivent tout seuls, `computePiece` les dérivant d'elle.
   *
   * Les lignes issues de la consigne sont marquées `consigne` : c'est ce
   * marquage qui, à l'enregistrement, devient la correction
   * réinjectable. Une gamme corrigée sans trace de ce qui l'a corrigée
   * n'apprend rien.
   */
  function appliquerGamme(index, lignes) {
    const gamme = gammeProposee(lignes, editor.pieces[index]?.gamme, 'consigne');
    // Le geste est DÉCLARÉ — c'est lui qui autorise la capture de la
    // correction à l'enregistrement. `patchPiece` s'en charge depuis la
    // clé du patch : écrire `gamme` déclare `gamme`, et rien d'autre.
    editor.patchPiece(index, { gamme });
  }

  /**
   * PROMOUVOIR une consigne en règle d'atelier — §7bis.
   *
   * Une consigne stockée sur la pièce n'est relue que pour les pièces de
   * signature proche. Une RÈGLE est chargée à chaque estimation. C'est
   * la différence entre un post-it et quelque chose que le programme
   * sait — et c'est ce qui fait qu'on ne refait plus l'erreur.
   *
   * Elle naît INACTIVE : la validation humaine est un geste distinct.
   * `input_text` porte la phrase EXACTE, jamais une reformulation — chez
   * AGC c'est la réécriture par l'IA qui était stockée et appliquée,
   * sans que personne puisse voir la dérive.
   */
  async function enFaireUneRegle() {
    const texte = String(revision?.consigne ?? editor.pieces[revision?.index]?.consigne_gamme ?? '').trim();
    if (!texte) return;
    try {
      await api.post('/regles', { scope: 'gamme', input_text: texte, structured: null });
      setRevision((r) => (r ? { ...r, regleCreee: true } : r));
      toast.success('Règle d’atelier créée — à valider dans Paramètres → Règles métier.');
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function changeStatus(status) {
    let lostReason = null;
    if (status === 'lost') {
      lostReason = window.prompt('Motif de la perte ?');
      if (!lostReason) return;
    }
    try {
      if (!(await enreg.vider())) throw new Error('Enregistrement impossible : statut non modifié.');
      await api.post(`/devis/${editor.devis.id}/statut`, { status, lost_reason: lostReason });
      toast.success('Statut mis à jour.');
      onReload();
    } catch (err) {
      toast.error(err.message);
    }
  }

  /**
   * Le devis supprimé, on QUITTE l'écran.
   *
   * Rester sur `/devis/:id` après coup afficherait une erreur de
   * chargement — donc un écran d'échec juste après une action réussie,
   * et le deviseur croirait que la suppression n'a pas marché. On
   * remonte à la liste, où il verra qu'elle a bien disparu.
   *
   * `editor.markSaved` n'a rien à sauver ici : on désarme la garde de
   * navigation pour qu'elle ne demande pas de conserver un devis qui
   * n'existe plus.
   */
  async function supprimerDevis() {
    enreg.liberer();
    editor.markSaved(editor.devis, []);
    toast.success('Devis supprimé.');
    navigate('/devis');
  }

  async function createRevision() {
    try {
      const res = await api.post(`/devis/${editor.devis.id}/revision`);
      // Le plan ne suit pas : les pages appartiennent au devis
      // d'origine, et les rattacher ferait qu'en le supprimant on
      // effacerait le plan de la révision. On le dit plutôt que de
      // laisser découvrir un visualiseur vide.
      toast.success(
        `Révision ${res.devis.reference} créée — les plans restent sur le devis d’origine.`,
      );
      navigate(`/devis/${res.devis.id}`);
    } catch (err) {
      toast.error(err.message);
    }
  }

  return (
    <>
      <DevisHeaderCard
        editor={editor}
        locked={locked}
        saving={saving}
        enregistrement={enreg.statut}
        onSave={save}
        onCreateRevision={createRevision}
        onSupprime={supprimerDevis}
      />

      <PanneauAssistant devisId={editor.devis.id} pieceId={courante?.piece?.id ?? null} pieceNom={courante?.piece?.name ?? null} />
      <PlanUploadCard
        devisId={editor.devis.id}
        disabled={locked}
        onDepose={traitement.suivre} onLu={onReload}
        onAvantSuppression={enreg.vider} onSupprime={recharger}
      />

      <ProgressionPages
        pages={traitement.pages}
        actif={traitement.actif}
        enCours={traitement.enCours}
        onRelancer={traitement.relancer}
        decoupe={locked ? null : decoupe}
      />

      <BarreChiffrage
        aChiffrer={locked ? 0 : aChiffrer}
        occupe={chiffrageDevis}
        onChiffrer={chiffrerTout}
      />

      {/* ── LA bande : une vignette par pièce, et la sélection ── */}
      <BandePieces
        pieces={editor.priced}
        selection={index}
        onSelect={setSelection}
        total={editor.totals.total_ht ?? editor.totals.total_usinage}
        aVerifier={aVerifier} pages={traitement.pages}
      />

      {/* ── Le plan DE LA PIÈCE SÉLECTIONNÉE ──
          Il suit la sélection, il ne se pilote plus séparément. C'est
          exactement ce qui rendait possible d'afficher le dessin d'une
          pièce et les nombres d'une autre. */}
      {pagesVues.length > 0 || courante ? (
        <>
          {enAttenteDePiece ? (
            <p className="field__hint" role="status">
              Plan déposé, <strong>lecture en cours</strong> — les pièces et les prix arrivent
              dessous au fur et à mesure. Vous pouvez déjà regarder le dessin.
            </p>
          ) : null}
          {/* ── LE PLAN ET LA FICHE, CÔTE À CÔTE ──
              Le deviseur décrit comment il usinerait la pièce : il doit
              avoir le DESSIN et la QUESTION sur le même écran. Ils
              étaient l'un sous l'autre, séparés par 1 000 px — donc on
              décrivait de mémoire, ou on ne décrivait pas.
              À droite : ce qui identifie la pièce — client, projet,
              référence, désignation, quantité, nature — et dessous le
              champ de consigne. */}
          {/* ── UNE PIÈCE SANS PLAN GARDE SON IDENTITÉ — 27 août 2026 ──
              Tout ce bloc était conditionné à l'existence d'une page de
              plan. Une pièce ajoutée à la main — un composant du
              commerce, une reprise — n'avait donc NI référence, NI
              désignation, NI quantité éditables, et depuis ce matin ni
              consigne ni bouton « Chiffrer la pièce ».
              Le dessin est facultatif ; l'identité de la pièce ne l'est
              pas. Le visualiseur reste conditionnel, la fiche non. */}
          {/* ── LA CONSIGNE AU-DESSUS DU PLAN, SUR TOUTE LA LARGEUR ──
              Charles, 15 septembre 2026 : dans le tiers droit, à côté du
              dessin, une consigne de dix lignes se lisait en colonne
              étroite. C'est le champ le plus utile de l'écran : on le
              lit d'abord, en entier, puis on regarde le plan. */}
          {courante && !courante.piece.is_purchase ? (
            <ConsigneGamme
              piece={courante.piece} machines={machines}
              disabled={locked}
              onChange={(patch) => editor.patchPiece(index, patch)}
              aUneGamme={(courante.piece.gamme ?? []).length > 0}
              onChiffrer={() => estimer(index)}
              chiffrage={estimating === index}
            />
          ) : null}
          <PlanEtFiche
            devisId={editor.devis.id}
            devis={editor.devis}
            pages={pagesVues}
            pageId={pageVue}
            onSelectPage={setPageDeposee}
            piece={courante?.piece ?? null}
            result={courante?.result ?? null}
            sources={editor.sources[index] ?? {}}
            materiaux={data.materiaux ?? []}
            disabled={locked}
            onRecharger={onReload} decoupe={decoupe}
            onChange={(patch) => editor.patchPiece(index, patch)}
          />

          {/* ── LE DIFF, ENTRE LE PLAN ET LA PIÈCE ──
              Là où on vient de poser la consigne, et avant tout le
              reste : c'est une décision à prendre, pas une information
              à parcourir. */}
          {revision && revision.index === index ? (
            <DiffGamme
              diff={revision.diff}
              consigne={courante?.piece?.consigne_gamme}
              busy={revising}
              onAbandonner={() => setRevision(null)}
              onEnFaireUneRegle={enFaireUneRegle}
              regleCreee={revision.regleCreee === true}
              epingles={revision.temps_epingles ?? []}
            />
          ) : null}
        </>
      ) : null}

      {courante ? (
        <PieceEditor
          key={courante.piece.id ?? `new-${index}`}
          piece={courante.piece}
          devisId={editor.devis.id}
          result={courante.result}
          tiers={courante.tiers}
          machines={machines}
          suppliers={suppliers}
          disabled={locked}
          onChange={(patch) => editor.patchPiece(index, patch)}
          onEstimer={() => estimer(index)}
          estimating={estimating === index}
          onReviser={(message) => reviser(index, message)}
          revising={revising}
          onRemove={() => editor.removePiece(index)}
          onDuplicate={() => editor.duplicatePiece(index)}
        />
      ) : (
        <Card>
          <CardBody>
            <p className="field__hint">
              {editor.priced.length === 0
                ? 'Aucune pièce sur ce devis. Déposez un plan : les pièces naissent de la lecture.'
                : 'Cette page n’a pas encore été lue.'}
            </p>
          </CardBody>
        </Card>
      )}

      {/* ── L'encart « Matière et transport » du DEVIS a été retiré ──
          Il portait un montant matière et un transport au niveau du
          devis, pour prévaloir sur la somme des pièces. Depuis que
          CHAQUE pièce porte les deux, il n'ajoutait qu'une troisième
          porte vers les mêmes valeurs — et personne ne saisit un montant
          matière global sur un devis de vingt-deux pièces.
          Les colonnes restent en base : un devis ancien qui en porte un
          continue de l'appliquer. C'est l'écran qui disparaît, pas la
          règle du §1.3. */}

      <DocumentsCard
        devisId={editor.devis.id}
        pieces={editor.pieces}
        payload={editor.payload}
        onSaved={onReload}
      />

      <Card>
        <CardBody>
          <div className="row row--between">
            {/* ── TROIS nombres, parce qu'il y en a trois ──
                « Total du devis » affichait la somme des prix d'USINAGE
                et s'appelait quand même « total », pendant que la
                synthèse de chaque pièce en affichait un autre qui, lui,
                comptait les quatre blocs. Deux totaux pour le même
                devis, sur le même écran, sans que rien ne dise lequel
                était lequel.
                Et « Coût de revient » n'existe plus : la notion a
                disparu le 5 août, quand le taux est devenu un prix de
                vente. Il n'y a plus « un coût puis un prix ». */}
            {/* ── Un total inconnu ne fait pas disparaître l'USINAGE ──
                Une seule nuance non rattachée rendait la somme hors
                engagement inconnue, donc le total, donc l'écran
                n'affichait plus rien — sur un devis dont le prix
                d'usinage était parfaitement calculé. C'est le défaut
                inverse de celui qu'on venait de corriger : l'un
                affichait un total trop bas d'apparence complète, l'autre
                cache un chiffre juste. On montre ce qu'on sait, et on
                dit ce qui manque. */}
            <div>
              <p className="caps">
                {editor.totals.total_ht == null ? 'Usinage — total incomplet' : 'Total du devis'}
              </p>
              <p className="devis-total num">
                {formatEuro(editor.totals.total_ht ?? editor.totals.total_usinage)} HT
              </p>
              <p className="field__hint">
                {editor.totals.total_ht == null ? (
                  <>
                    C’est ce que Mertens <strong>engage</strong>. La matière n’a pas de prix sur{' '}
                    <strong>{editor.totals.pieces_sans_prix_matiere} pièce(s)</strong> : rattachez
                    leur nuance pour obtenir le total.
                    {editor.totals.hors_engagement_partiel ? (
                      <>
                        {' '}Le reste des lignes indicatives fait déjà{' '}
                        {formatEuro(editor.totals.hors_engagement_partiel)}.
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    Usinage <strong>{formatEuro(editor.totals.total_usinage)}</strong>, ce que
                    Mertens engage · matière, traitements et transport{' '}
                    <strong>{formatEuro(editor.totals.total_hors_engagement)}</strong>, indicatif
                  </>
                )}{' '}
                —{' '}
                {editor.totals.incomplete.length > 0
                  ? `${editor.totals.incomplete.length} pièce(s) incomplète(s)`
                  : 'toutes les pièces sont chiffrables'}
              </p>
            </div>
            <div className="row">
              <Button variant="secondary" onClick={editor.addPiece} disabled={locked}>
                Ajouter une pièce
              </Button>
              <Select
                label="Statut"
                value={editor.devis.status}
                options={OPTIONS_STATUT}
                onChange={(e) => changeStatus(e.target.value)}
              />
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

export default DevisPage;
